import { clsx } from "clsx";
import { InfoIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/ui/button";
import { formatIsoDate } from "../../../../lib/format";
import { formatCurrency, formatSignedCurrency } from "../../../../lib/money";
import LineItemRow from "../../components/LineItemRow";
import Section from "../../components/Section";
import {
  calculateAdvanceTotal,
  calculateAmountPaidToDealerToday,
  calculateDifferenceInAmountPaid,
} from "./DealStatus.calculations";

type DealStatusLineItemLabelKey =
  | "dealStatus.lineItems.vehicleAdvance"
  | "dealStatus.lineItems.capsAdvantage"
  | "dealStatus.lineItems.vehicleServiceContract"
  | "dealStatus.lineItems.vscNetDealerCost"
  | "dealStatus.lineItems.vscProfit"
  | "dealStatus.lineItems.gap"
  | "dealStatus.lineItems.gapNetDealerCost"
  | "dealStatus.lineItems.gapProfit"
  | "dealStatus.lineItems.dealFees";

type DealStatusMilestoneLabelKey =
  | "dealStatus.milestones.dealSubmitted"
  | "dealStatus.milestones.dealPackageReceived"
  | "dealStatus.milestones.dscReviewInitiated"
  | "dealStatus.milestones.dealHeld"
  | "dealStatus.milestones.dealFunded";

interface DealStatusSubItem {
  labelKey: DealStatusLineItemLabelKey;
  amount: number;
}

interface DealStatusLineItem {
  labelKey: DealStatusLineItemLabelKey;
  // Positive amounts add to the group's total, negative amounts subtract —
  // the operation lives in the sign of the value itself, not in which group
  // (Advance items vs. deductions) the item happens to be rendered under, so
  // a group isn't forced to be all-addition or all-subtraction.
  amount: number;
  // Only consulted when `amount` is exactly 0 (see formatSignedCurrency) —
  // every nonzero amount's sign already speaks for itself.
  negativeZero?: boolean;
  subItems?: DealStatusSubItem[];
  isLink?: boolean;
}

interface DealStatusMilestone {
  labelKey: DealStatusMilestoneLabelKey;
  date?: string;
}

// Mock data standing in for the real deal-status payload until this section
// is wired up to the dashboard response. Amounts are internally consistent
// with each other (Advance = sum of these items; Amount Paid to Dealer Today
// = Advance plus the deductions below, whose amounts are already negative)
// to mirror how the real figures will reconcile.
const MOCK_ADVANCE_ITEMS: DealStatusLineItem[] = [
  { labelKey: "dealStatus.lineItems.vehicleAdvance", amount: 4552.08 },
  { labelKey: "dealStatus.lineItems.capsAdvantage", amount: 100 },
  {
    labelKey: "dealStatus.lineItems.vehicleServiceContract",
    amount: 1901,
    subItems: [
      { labelKey: "dealStatus.lineItems.vscNetDealerCost", amount: 1327 },
      { labelKey: "dealStatus.lineItems.vscProfit", amount: 574 },
    ],
  },
  {
    labelKey: "dealStatus.lineItems.gap",
    amount: 0,
    subItems: [
      { labelKey: "dealStatus.lineItems.gapNetDealerCost", amount: 0 },
      { labelKey: "dealStatus.lineItems.gapProfit", amount: 0 },
    ],
  },
];

const MOCK_ADVANCE_TOTAL = calculateAdvanceTotal(MOCK_ADVANCE_ITEMS);

// Deduction amounts are negative — each one subtracts from the Advance total
// by virtue of its own sign (see DealStatusLineItem), not because this list
// is treated as "always subtract". GAP Net Dealer Cost is a $0 deduction, so
// `negativeZero` forces its display to "-" (see formatSignedCurrency) even
// though $0 doesn't change the total either way.
const MOCK_DEDUCTIONS: DealStatusLineItem[] = [
  { labelKey: "dealStatus.lineItems.dealFees", amount: -495, isLink: true },
  { labelKey: "dealStatus.lineItems.vscNetDealerCost", amount: -1327 },
  { labelKey: "dealStatus.lineItems.gapNetDealerCost", amount: 0, negativeZero: true },
];

const MOCK_AMOUNT_PAID_TO_DEALER_TODAY = calculateAmountPaidToDealerToday(MOCK_ADVANCE_TOTAL, MOCK_DEDUCTIONS);
// Amount Expected at Submit is an independent input (not derived from the
// items above) — stays a standalone mock value until it's wired to real data.
const MOCK_AMOUNT_EXPECTED_AT_SUBMIT = 4731.08;
const MOCK_DIFFERENCE_IN_AMOUNT_PAID = calculateDifferenceInAmountPaid(
  MOCK_AMOUNT_EXPECTED_AT_SUBMIT,
  MOCK_AMOUNT_PAID_TO_DEALER_TODAY
);

const MOCK_MILESTONES: DealStatusMilestone[] = [
  { labelKey: "dealStatus.milestones.dealSubmitted", date: "2026-03-06" },
  { labelKey: "dealStatus.milestones.dealPackageReceived", date: "2026-03-06" },
  { labelKey: "dealStatus.milestones.dscReviewInitiated", date: "2026-03-06" },
  { labelKey: "dealStatus.milestones.dealHeld" },
  { labelKey: "dealStatus.milestones.dealFunded" },
];

// CSS class for underlined rows in calculations section
const underlineClassName = "border-border border-t border-dashed";

function DealStatus() {
  const { t } = useTranslation();

  const handleCapsHistoryClick = () => {
    window.alert(t("dealStatus.capsHistoryPlaceholderAlert"));
  };

  const handleDealFeesClick = () => {
    window.alert(t("dealStatus.dealFeesPlaceholderAlert"));
  };

  return (
    <Section title={t("dealStatus.sectionTitle")}>
      <div className="gap-x-(--spacing-8) gap-y-(--spacing-4) grid grid-cols-1 sm:grid-cols-2">
        <div className="flex flex-col">
          {/* Top calculations */}
          <div className="gap-(--spacing-1) flex flex-col">
            {MOCK_ADVANCE_ITEMS.map((item) => (
              <div key={item.labelKey}>
                <LineItemRow label={t(item.labelKey)} value={formatSignedCurrency(item.amount, item.negativeZero)} />
                {item.subItems?.map((subItem) => (
                  <LineItemRow
                    key={subItem.labelKey}
                    label={t(subItem.labelKey)}
                    value={formatCurrency(subItem.amount)}
                    indent
                  />
                ))}
              </div>
            ))}
          </div>

          {/* Total */}
          <LineItemRow
            label={t("dealStatus.advance")}
            value={formatCurrency(MOCK_ADVANCE_TOTAL)}
            className={clsx("font-(--font-weight-bold) mt-(--spacing-2) pt-(--spacing-2)", underlineClassName)}
            valueClassName="text-primary"
          />

          {/* Deductions */}
          <div className="gap-(--spacing-1) mt-(--spacing-1) flex flex-col">
            {MOCK_DEDUCTIONS.map((item) => (
              <LineItemRow
                key={item.labelKey}
                label={
                  item.isLink ? (
                    <button
                      type="button"
                      onClick={handleDealFeesClick}
                      className="text-primary cursor-pointer text-left underline underline-offset-4"
                    >
                      {t(item.labelKey)}
                    </button>
                  ) : (
                    t(item.labelKey)
                  )
                }
                value={formatSignedCurrency(item.amount, item.negativeZero)}
              />
            ))}
          </div>

          {/* Amount paid to dealer today */}
          <LineItemRow
            label={t("dealStatus.amountPaidToDealerToday")}
            value={formatCurrency(MOCK_AMOUNT_PAID_TO_DEALER_TODAY)}
            className={clsx("font-(--font-weight-bold) mt-(--spacing-2) pt-(--spacing-2)", underlineClassName)}
            valueClassName="text-primary"
          />

          {/* Amount expected and difference */}
          <div
            className={clsx("mt-(--spacing-2) pt-(--spacing-2) gap-(--spacing-1) flex flex-col", underlineClassName)}
          >
            <LineItemRow
              label={t("dealStatus.amountExpectedAtSubmit")}
              value={formatCurrency(MOCK_AMOUNT_EXPECTED_AT_SUBMIT)}
            />
            <LineItemRow
              label={t("dealStatus.differenceInAmountPaid")}
              value={formatCurrency(MOCK_DIFFERENCE_IN_AMOUNT_PAID)}
            />
          </div>
        </div>

        {/* Milestones */}
        <div className="gap-(--spacing-1) flex flex-col">
          {MOCK_MILESTONES.map((milestone) => (
            <LineItemRow
              key={milestone.labelKey}
              label={t(milestone.labelKey)}
              value={milestone.date ? formatIsoDate(milestone.date) : "-"}
              labelClassName="font-(--font-weight-semibold)"
            />
          ))}
        </div>
      </div>

      {/* Footnotes */}
      <div className="border-border mt-(--spacing-4) pt-(--spacing-3) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) gap-(--spacing-1) flex flex-col border-t">
        <p>{t("dealStatus.advanceFootnote")}</p>
        <p>{t("dealStatus.amountPaidFootnote")}</p>
      </div>

      {/* Caps history link */}
      <Button
        type="button"
        variant="link"
        size="sm"
        onClick={handleCapsHistoryClick}
        className="mt-(--spacing-2) gap-(--spacing-1) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) h-auto p-0 underline"
      >
        {t("dealStatus.capsHistoryLink")}
        <InfoIcon className="size-3.5" aria-hidden="true" />
      </Button>
    </Section>
  );
}

export default DealStatus;
